'use client'
import { jsx } from "react/jsx-runtime";
import { DatePicker } from "@pcln/horizon";
import { addMonths } from "date-fns/addMonths";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { useCallback, useEffect, useRef, useState } from "react";
import { getPricingData } from "./getPricingData/getPricingData.js";
import { pricePromise as helper_js_pricePromise, processRecords } from "./helper/helper.js";
import FooterLeftNode from "./FooterLeftNode.js";
const getLowestPrice = (priceMap)=>Math.min(...Object.keys(priceMap).map((k)=>priceMap[k].price));
function HorizonFlyDatePicker({ dayMap: dayMapProps = {}, flightDetails, startDate, variantName, pricePromise: pricePromiseProps, getGraphURL, apolloClientInfo, selected, mode = 'range', numberOfMonths = 2, defaultMonth, startMonth, endMonth, title, label, handleCalendarChange, ...restProps }) {
    const pricePromise = pricePromiseProps || helper_js_pricePromise;
    const executedMonths = useRef({});
    const [dayMap, setDayMap] = useState(dayMapProps);
    const [lowestPrice, setLowestPrice] = useState(0);
    const handleCalendarDatesChange = (dates, triggerDate)=>{
        if (!dates) return;
        executedMonths.current = {};
        let displayedFromMonth;
        let displayedToMonth;
        let departDate;
        if ('range' === mode && 'from' in dates && dates.from && dates.to) {
            displayedFromMonth = format(dates.from, 'yyyyMM');
            displayedToMonth = format(dates.to, 'yyyyMM');
            departDate = format(dates.from, 'yyyyMMdd');
        } else {
            displayedFromMonth = format(dates, 'yyyyMM');
            displayedToMonth = displayedFromMonth;
        }
        const displayMonths = {
            displayedFromMonth: displayedFromMonth,
            displayedToMonth: displayedToMonth
        };
        getAirPricedCalendarData({
            displayMonths,
            clearDayMap: true,
            departDate
        });
        handleCalendarChange?.(dates, triggerDate);
    };
    const getAirPricedCalendarData = useCallback(({ displayMonths, clearDayMap, departDate })=>{
        getPricingData({
            displayMonths,
            flightDetails,
            pricePromise,
            variantName,
            executedMonths,
            departDate,
            getGraphURL,
            apolloClientInfo
        }).then((apiRecords)=>{
            if (!apiRecords) return;
            const processedRecords = processRecords({
                apiRecords: apiRecords,
                doubleCalendarPricing: false
            });
            const newState = {
                dayMap: clearDayMap ? {
                    ...processedRecords
                } : {
                    ...processedRecords,
                    ...dayMap
                }
            };
            setDayMap(newState.dayMap);
            setLowestPrice(getLowestPrice(newState.dayMap));
        }).catch(()=>{
            const { displayedFromMonth, displayedToMonth } = displayMonths;
            let fromMonth = parse(displayedFromMonth, 'yyyyMM', new Date());
            const toMonth = parse(displayedToMonth, 'yyyyMM', new Date());
            while(fromMonth.valueOf() <= toMonth.valueOf()){
                const fromMonthKey = format(fromMonth, 'yyyyMM');
                delete executedMonths.current[fromMonthKey];
                fromMonth = addMonths(fromMonth, 1);
            }
        });
    }, [
        flightDetails,
        pricePromiseProps,
        variantName,
        getGraphURL,
        apolloClientInfo
    ]);
    const onMonthChange = (month)=>{
        executedMonths.current = {};
        const displayedFromMonth = format(month, 'yyyyMM');
        const displayedToMonth = format(addMonths(month, numberOfMonths), 'yyyyMM');
        const displayMonths = {
            displayedFromMonth: displayedFromMonth,
            displayedToMonth: displayedToMonth
        };
        getAirPricedCalendarData({
            displayMonths,
            clearDayMap: true
        });
    };
    useEffect(()=>{
        const date = startDate ? new Date(startDate) : new Date();
        getAirPricedCalendarData({
            displayMonths: {
                displayedFromMonth: format(date, 'yyyyMM'),
                displayedToMonth: format(date, 'yyyyMM')
            },
            clearDayMap: true
        });
    }, []);
    return /*#__PURE__*/ jsx(DatePicker, {
        title: title,
        label: label,
        mode: mode,
        selected: selected,
        defaultMonth: defaultMonth,
        startMonth: startMonth,
        endMonth: endMonth,
        dayMap: {
            data: dayMap,
            variant: 'dayHeatMap'
        },
        lowestPrice: lowestPrice,
        numberOfMonths: numberOfMonths,
        handleCalendarChange: handleCalendarDatesChange,
        onMonthChange: onMonthChange,
        footerLeftNode: /*#__PURE__*/ jsx(FooterLeftNode, {
            lowestPrice: lowestPrice
        }),
        ...restProps
    });
}
export { HorizonFlyDatePicker };
